/**
 * Confirm Modal
 *
 * Specialized modal for confirmation dialogs (delete, submit, etc.)
 */

import { useRef, useState } from 'react';
import Box from '@mui/material/Box';

import type { ReactNode } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';

import { ModalBase } from './ModalBase';

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string | ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: 'primary' | 'error' | 'warning' | 'info' | 'success';
  icon?: SvgIconComponent;
  loading?: boolean;
  cancelAutoFocus?: boolean;
  confirmDisabled?: boolean;
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor = 'primary',
  icon,
  loading = false,
  cancelAutoFocus = false,
  confirmDisabled = false,
}: ConfirmModalProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const confirmInFlightRef = useRef(false);

  const handleConfirm = async () => {
    if (loading || confirmInFlightRef.current) return;
    confirmInFlightRef.current = true;
    setIsConfirming(true);
    try {
      await onConfirm();
    } finally {
      confirmInFlightRef.current = false;
      setIsConfirming(false);
    }
  };

  const isBusy = loading || isConfirming;

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title={title}
      icon={icon}
      iconColor={confirmColor}
      maxWidth="xs"
      // Keep content visible; only the confirm action shows loading.
      // We still prevent accidental close while processing.
      showCloseIcon={!isBusy}
      disableBackdropClick={isBusy}
      disableEscapeKeyDown={isBusy}
      actions={[
        { label: cancelLabel, onClick: onClose, disabled: isBusy, autoFocus: cancelAutoFocus },
        {
          label: confirmLabel,
          onClick: handleConfirm,
          variant: 'contained',
          color: confirmColor,
          loading: isBusy,
          disabled: confirmDisabled || isBusy,
        },
      ]}
    >
      <Box sx={{ py: 1 }}>{typeof message === 'string' ? <Box sx={{ color: 'text.primary' }}>{message}</Box> : message}</Box>
    </ModalBase>
  );
}
